import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import Lightbox from "@/components/Lightbox";
import { toast } from "sonner";
import { Loader2, Check, X, Images, Undo2, Eye } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const TABS = [
  { key: "submitted", label: "Pending Review" },
  { key: "published", label: "Published" },
  { key: "rejected", label: "Rejected" },
];

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("submitted");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [lightbox, setLightbox] = useState(-1);
  const [rejectFor, setRejectFor] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) navigate("/");
  }, [authLoading, user, navigate]);

  const load = async (status) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/collections", { params: { status } });
      setData((d) => ({ ...d, [status]: res.data }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load(tab);
  }, [tab, user]); // eslint-disable-line

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/collections/${id}/approve`);
      toast.success("Published");
      setPreview(null);
      load(tab);
    } catch (e) { toast.error("Failed"); } finally { setBusy(null); }
  };

  const doReject = async () => {
    setBusy(rejectFor);
    try {
      await api.post(`/admin/collections/${rejectFor}/reject`, { note });
      toast.success("Sent back to curator");
      setRejectFor(null); setNote(""); setPreview(null);
      load(tab);
    } catch (e) { toast.error("Failed"); } finally { setBusy(null); }
  };

  const unpublish = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/collections/${id}/unpublish`);
      toast.success("Unpublished");
      load(tab);
    } catch (e) { toast.error("Failed"); } finally { setBusy(null); }
  };

  if (authLoading || !user?.is_admin) return null;

  const list = data[tab] || [];

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl sm:text-4xl font-serif font-bold text-ink">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">Review curator submissions and publish collections.</p>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList data-testid="admin-tabs">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`admin-tab-${t.key}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-6">
              {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brick" /></div>
              ) : list.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-stoneline rounded-xl bg-white/50 text-muted-foreground">
                  Nothing here.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {list.map((c) => (
                    <div key={c.id} data-testid="admin-collection-row" className="bg-white border border-stoneline rounded-xl overflow-hidden shadow-sm flex">
                      <div className="w-32 sm:w-40 shrink-0 bg-stone-100">
                        {c.cover_image ? <img src={c.cover_image} alt="" className="h-full w-full object-cover" /> :
                          <div className="h-full flex items-center justify-center"><Images className="h-8 w-8 text-stone-300" /></div>}
                      </div>
                      <div className="p-4 flex-1 min-w-0 flex flex-col">
                        <div className="flex items-center gap-2">
                          <h3 className="font-serif text-lg font-semibold text-ink truncate">{c.title}</h3>
                          <StatusBadge status={c.status} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">by {c.owner_name} · {c.items.length} images</p>
                        {c.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>}
                        <div className="mt-auto pt-3 flex flex-wrap items-center gap-2">
                          <button data-testid={`review-btn-${c.id}`} onClick={() => { setPreview(c); setLightbox(-1); }} className="inline-flex items-center gap-1.5 border border-stoneline hover:border-brick px-3 py-1.5 rounded-full text-xs font-semibold transition-colors">
                            <Eye className="h-3.5 w-3.5" /> Review
                          </button>
                          {c.status !== "published" && (
                            <button data-testid={`approve-btn-${c.id}`} onClick={() => approve(c.id)} disabled={busy === c.id} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-50">
                              <Check className="h-3.5 w-3.5" /> Publish
                            </button>
                          )}
                          {c.status === "submitted" && (
                            <button data-testid={`reject-btn-${c.id}`} onClick={() => { setRejectFor(c.id); setNote(""); }} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors">
                              <X className="h-3.5 w-3.5" /> Reject
                            </button>
                          )}
                          {c.status === "published" && (
                            <button data-testid={`unpublish-btn-${c.id}`} onClick={() => unpublish(c.id)} disabled={busy === c.id} className="inline-flex items-center gap-1.5 border border-stoneline hover:border-amber-400 text-amber-700 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-50">
                              <Undo2 className="h-3.5 w-3.5" /> Unpublish
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="admin-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              {preview?.title} {preview && <StatusBadge status={preview.status} />}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <p className="text-sm text-muted-foreground">by {preview.owner_name} · {preview.items.length} images</p>
              {preview.description && <p className="text-sm">{preview.description}</p>}
              <div className="masonry columns-2 sm:columns-3 mt-2">
                {preview.items.map((item, i) => (
                  <img key={item.item_id} src={item.image_url} alt={item.title} onClick={() => setLightbox(i)} className="w-full rounded-lg border border-stoneline cursor-zoom-in" />
                ))}
              </div>
              <DialogFooter className="gap-2">
                {preview.status !== "published" && (
                  <button onClick={() => approve(preview.id)} disabled={busy === preview.id} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
                    <Check className="h-4 w-4" /> Publish
                  </button>
                )}
                {preview.status === "submitted" && (
                  <button onClick={() => { setRejectFor(preview.id); setNote(""); }} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-5 py-2.5 rounded-full text-sm font-semibold">
                    <X className="h-4 w-4" /> Reject
                  </button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Reject with feedback</DialogTitle></DialogHeader>
          <Textarea data-testid="reject-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain what the curator should change…" rows={4} />
          <DialogFooter>
            <button data-testid="confirm-reject-btn" onClick={doReject} disabled={busy === rejectFor} className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
              {busy === rejectFor ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send feedback
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {preview && lightbox >= 0 && (
        <Lightbox items={preview.items} index={lightbox} onClose={() => setLightbox(-1)} onNav={(d) => setLightbox((p) => (p + d + preview.items.length) % preview.items.length)} />
      )}
    </div>
  );
}
